import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableActionIcon from '@/components/ui/ActionIcon/TableActionIcon';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import CustomMenu from '@/components/ui/Menu';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import styles from './SupportRequestsPage.module.scss';

/**
 * Download button for the אסמכתא (transfer receipt) - only rendered for one-time requests
 * whose status is exactly Done (the eligibility check lives on the page, which needs the
 * same answer for the mobile sheet).
 *
 * HAT-1253: this used to be a bespoke solid brand-coloured 34px disc with the icon forced
 * white by a `filter` hack, which read as a different species of control from the two
 * transparent round icon-buttons beside it. It now goes through the shared TableActionIcon
 * and borrows the transactions board's download glyph, so the whole row speaks one language.
 */
const ReceiptDownloadButton = ({ fundId, request }: { fundId: number; request: SupportRequestDtoIn }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFileFromApi(
        `funds/${fundId}/support-requests/${request.correlationId}/receipt`,
        `אסמכתא-${getShortRequestId(request.correlationId)}.pdf`,
        currentUser?.token ?? ''
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <TableActionIcon title={t('downloadTransferReceipt')} disabled={isDownloading} onClick={() => void handleDownload()}>
      {isDownloading ? <span className={styles.receiptSpinner} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />}
    </TableActionIcon>
  );
};

interface SupportRequestRowActionsProps {
  request: SupportRequestDtoIn;
  /** Undefined until the fund context has resolved; the receipt route needs it. */
  fundId?: number;
  /** The אסמכתא download is the one action this board drops on narrow screens (HAT-1170). */
  isMobile: boolean;
  isReceiptEligible: boolean;
  menuItems: { icon?: string; text: string; onClick: () => void }[];
}

/**
 * The row's action icons: אסמכתא download, opening an inquiry, and the per-row menu.
 *
 * HAT-1253 - three slots of the same size, always all three.
 *
 * Both the אסמכתא button and the menu are per-row conditional (a receipt exists only for a
 * finished one-time request; the menu only where an action is still open), and they were
 * originally rendered with `&&` straight into the flex row. In RTL that means whatever
 * survives repacks against the right edge - so the פנייה bubble, the one icon every row has,
 * landed at a different x depending on its neighbours, and the column read as ragged.
 *
 * The first pass at this reserved an *empty* slot for an absent action. That straightened
 * every column, but left the board looking holey: each row still showed a different number
 * of icons, with the gaps landing on different sides. Which is what the reporter was still
 * seeing, and why this second pass exists.
 *
 * An unavailable receipt is now drawn in full and disabled, exactly as the transactions board
 * does for a transaction whose receipt has not been issued, and its `title` says why.
 *
 * HAT-1405: the menu is the exception. The office asked for the three dots to be gone from a
 * row that has nothing left to do, as they were before - a greyed menu on every closed request
 * read as one more thing that might still be done to it. Its 34px slot stays, empty: the menu is
 * the outermost icon, so the gap lands at the end of the row and the others do not move.
 */
const SupportRequestRowActions = ({ request, fundId, isMobile, isReceiptEligible, menuItems }: SupportRequestRowActionsProps) => {
  const { t } = useTranslation();
  const showReceipt = isReceiptEligible && !!fundId;

  return (
    <div className="flex items-center gap-[6px] flex-nowrap">
      {!isMobile &&
        (showReceipt && fundId ? (
          <ReceiptDownloadButton fundId={fundId} request={request} />
        ) : (
          <TableActionIcon title={t('transferReceiptUnavailable')} disabled onClick={() => {}}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />
          </TableActionIcon>
        ))}
      <OpenInquiryButton resourceType="SupportRequest" resourceId={request.correlationId} size="medium" />
      <span className={styles.menuSlot} data-testid="row-menu-slot">
        {menuItems.length > 0 && <CustomMenu items={menuItems} />}
      </span>
    </div>
  );
};

export default SupportRequestRowActions;
